import React from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, TextField,
  FormControlLabel, Switch, Typography, Box, Divider, Accordion, AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import { useImagePicker } from './MediaPicker';

// Per-page SEO / social-share editor. Writes into the page's `seo` blob (a free
// JSON object), which apps/web turns into <title>, meta description, canonical,
// robots, Open Graph, Twitter Card and JSON-LD structured data. Every field is
// optional — sensible defaults are derived at render time.
export type Seo = Record<string, unknown>;

export function SeoDialog({
  open, onClose, seo, onChange, slug,
}: { open: boolean; onClose: () => void; seo: Seo; onChange: (s: Seo) => void; slug: string }) {
  const picker = useImagePicker();
  const s = (k: string) => (typeof seo[k] === 'string' ? (seo[k] as string) : '');
  const set = (k: string, v: unknown) => {
    const next = { ...seo };
    if (v === '' || v === undefined || v === false) delete next[k];
    else next[k] = v;
    onChange(next);
  };
  const title = s('title');
  const description = s('description');

  const counter = (val: string, ideal: number) => (
    <Typography variant="caption" color={val.length > ideal ? 'warning.main' : 'text.secondary'}>
      {val.length}/{ideal} recommended
    </Typography>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>SEO &amp; social — <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}>/{slug}</Box></DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5} sx={{ mt: 0.5 }}>
          <Box>
            <TextField
              label="Meta title" fullWidth size="small" value={title}
              onChange={(e) => set('title', e.target.value)}
              helperText="Shown as the clickable headline in Google results and the browser tab."
            />
            <Box sx={{ textAlign: 'right', mt: 0.5 }}>{counter(title, 60)}</Box>
          </Box>
          <Box>
            <TextField
              label="Meta description" fullWidth size="small" multiline minRows={2} value={description}
              onChange={(e) => set('description', e.target.value)}
              helperText="The grey summary line under the title in search results."
            />
            <Box sx={{ textAlign: 'right', mt: 0.5 }}>{counter(description, 155)}</Box>
          </Box>
          <TextField
            label="Focus keywords (comma-separated)" fullWidth size="small" value={s('keywords')}
            onChange={(e) => set('keywords', e.target.value)}
            placeholder="identity security, IAM, workforce identity"
          />

          <Divider textAlign="left"><Typography variant="caption" color="text.secondary">Social share card</Typography></Divider>
          <Box>
            <TextField
              label="Share image URL" fullWidth size="small" value={s('ogImage')}
              onChange={(e) => set('ogImage', e.target.value)}
              helperText="Used when the page is shared on LinkedIn/X/Slack. Ideal 1200×630. Defaults to the first image on the page."
            />
            {picker ? (
              <Button size="small" startIcon={<PhotoLibraryIcon />} sx={{ mt: 0.5 }}
                onClick={async () => { const url = await picker.pick(); if (url) set('ogImage', url); }}>
                Browse / upload
              </Button>
            ) : null}
            {/^(https?:\/\/|\/)/.test(s('ogImage')) ? (
              <Box component="img" src={s('ogImage')} alt="" sx={{ display: 'block', maxHeight: 90, mt: 1, borderRadius: 1, border: '1px solid', borderColor: 'divider' }} />
            ) : null}
          </Box>
          <TextField
            label="Share image alt text" fullWidth size="small" value={s('ogImageAlt')}
            onChange={(e) => set('ogImageAlt', e.target.value)}
          />

          <FormControlLabel
            control={<Switch checked={seo.noindex === true} onChange={(e) => set('noindex', e.target.checked)} />}
            label="Hide this page from search engines (noindex)"
          />

          <Accordion disableGutters elevation={0} sx={{ '&:before': { display: 'none' } }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0 }}>
              <Typography variant="body2" color="text.secondary">Advanced</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 0 }}>
              <Stack spacing={2}>
                <TextField
                  label="Canonical URL (override)" fullWidth size="small" value={s('canonical')}
                  onChange={(e) => set('canonical', e.target.value)}
                  helperText="Leave blank to use this page's own URL. Set only to point search engines at a different primary URL."
                />
                <TextField label="Open Graph title (override)" fullWidth size="small" value={s('ogTitle')} onChange={(e) => set('ogTitle', e.target.value)} />
                <TextField label="Open Graph description (override)" fullWidth size="small" multiline minRows={2} value={s('ogDescription')} onChange={(e) => set('ogDescription', e.target.value)} />
                <TextField label="Twitter @handle" fullWidth size="small" value={s('twitterSite')} onChange={(e) => set('twitterSite', e.target.value)} placeholder="@cymmetri" />
              </Stack>
            </AccordionDetails>
          </Accordion>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Done</Button>
      </DialogActions>
    </Dialog>
  );
}
